import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck, AlertTriangle, Lock, Star, Anchor, Calendar,
  Mail, FileSignature, Fish, ArrowRight,
} from "lucide-react";
import { Button } from "../components/ui/button";

export default function Confiance() {
  useEffect(() => { document.title = "Le badge de confiance — Le Bon Matelot"; }, []);
  return (
    <div className="bg-sable/60 min-h-screen">
      <div className="max-w-3xl mx-auto px-6 pt-12 pb-20">
        <div className="inline-flex items-center bg-bretagne/15 text-bretagne text-[11px] font-bold uppercase tracking-widest px-3 py-1.5 rounded-full">
          Confiance
        </div>
        <h1 className="mt-4 font-display text-3xl md:text-5xl font-bold text-ocean tracking-tight leading-[1.1]">
          Le badge de confiance Le&nbsp;Bon&nbsp;Matelot
        </h1>
        <p className="mt-4 text-ocean/75 max-w-2xl leading-relaxed">
          La transparence avant tout : voici ce que la vérification garantit, ses limites,
          et comment vos données sont protégées.
        </p>

        <div className="mt-6 inline-flex items-center gap-2">
          <span data-testid="badge-pill" className="inline-flex items-center gap-1.5 bg-turquoise/20 text-bretagne text-sm font-bold px-3 py-1.5 rounded-full">
            <ShieldCheck className="w-4 h-4" /> Profil vérifié
          </span>
          <span className="text-sm text-ocean/70">
            Documents fournis par le capitaine et contrôlés par Le Bon Matelot.
          </span>
        </div>

        {/* Ce que le badge signifie */}
        <section className="mt-8 bg-blanc border border-brume rounded-2xl p-6">
          <h2 className="font-display text-xl md:text-2xl font-bold text-ocean inline-flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-turquoise" /> Ce que le badge signifie
          </h2>
          <p className="mt-2 text-sm text-ocean/80">
            Le Bon Matelot a reçu et contrôlé manuellement les documents fournis par le capitaine :
          </p>
          <ul className="mt-3 space-y-2 text-sm text-ocean/85">
            <li className="flex gap-2"><span className="text-bretagne">•</span> Pièce d'identité</li>
            <li className="flex gap-2"><span className="text-bretagne">•</span> Permis plaisance (si le bateau le requiert)</li>
            <li className="flex gap-2"><span className="text-bretagne">•</span> Immatriculation du bateau</li>
            <li className="flex gap-2"><span className="text-bretagne">•</span> Attestation d'assurance couvrant les passagers</li>
          </ul>
        </section>

        {/* Ce que le badge ne signifie pas */}
        <section className="mt-4 bg-corail/8 border border-corail/30 rounded-2xl p-6">
          <h2 className="font-display text-xl md:text-2xl font-bold text-ocean inline-flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-corail" /> Ce que le badge ne signifie pas
          </h2>
          <p className="mt-3 text-sm text-ocean/85 leading-relaxed">
            Le Bon Matelot <strong>n'est ni l'État ni un assureur</strong>. Le badge atteste qu'un
            contrôle de base a été effectué — il ne garantit <strong>pas</strong> la sécurité d'une
            sortie, ni la validité absolue ou permanente des documents. Chacun reste responsable
            de naviguer prudemment et de vérifier que la sortie correspond à son niveau.
          </p>
        </section>

        {/* Socle commun */}
        <section className="mt-4 bg-blanc border border-brume rounded-2xl p-6">
          <h2 className="font-display text-xl md:text-2xl font-bold text-ocean">
            Le socle commun à tous les capitaines
          </h2>
          <p className="mt-1 text-sm text-ocean/70">
            Obligatoire pour publier une sortie, même sans badge :
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            {[
              { i: Mail, l: "E-mail vérifié" },
              { i: FileSignature, l: "Charte acceptée" },
            ].map(({ i: Icon, l }) => (
              <span key={l} className="inline-flex items-center gap-1.5 bg-turquoise/15 text-bretagne text-sm font-bold px-3 py-1.5 rounded-full">
                <Icon className="w-3.5 h-3.5" /> {l}
              </span>
            ))}
          </div>
        </section>

        {/* Autres signaux */}
        <section className="mt-4 bg-blanc border border-brume rounded-2xl p-6">
          <h2 className="font-display text-xl md:text-2xl font-bold text-ocean">
            Les autres signaux de confiance
          </h2>
          <div className="mt-4 grid sm:grid-cols-2 gap-3">
            {[
              { i: Star, l: "Note moyenne et avis des sorties passées" },
              { i: Anchor, l: "Nombre de sorties réalisées" },
              { i: Calendar, l: "Ancienneté du compte" },
              { i: ShieldCheck, l: "Pictogramme e-mail vérifié" },
            ].map(({ i: Icon, l }) => (
              <div key={l} className="bg-sable border border-brume rounded-xl px-3 py-2.5 inline-flex items-center gap-2 text-sm text-ocean/85">
                <Icon className="w-4 h-4 text-bretagne shrink-0" />
                <span>{l}</span>
              </div>
            ))}
          </div>
        </section>

        {/* RGPD */}
        <section className="mt-4 bg-blanc border border-brume rounded-2xl p-6">
          <h2 className="font-display text-xl md:text-2xl font-bold text-ocean inline-flex items-center gap-2">
            <Lock className="w-5 h-5 text-bretagne" /> Vos documents &amp; vos données (RGPD)
          </h2>
          <ul className="mt-3 space-y-2 text-sm text-ocean/85 leading-relaxed">
            <li className="flex gap-2">
              <span className="text-bretagne">•</span>
              <span>Traités uniquement avec votre <strong>consentement explicite</strong>, dans le seul but de la vérification.</span>
            </li>
            <li className="flex gap-2">
              <span className="text-bretagne">•</span>
              <span><strong>Minimisation</strong> : seuls les documents nécessaires sont demandés.</span>
            </li>
            <li className="flex gap-2">
              <span className="text-bretagne">•</span>
              <span><strong>Stockés de façon sécurisée</strong>, accès restreint à l'équipe de modération.</span>
            </li>
            <li className="flex gap-2">
              <span className="text-bretagne">•</span>
              <span><strong>Jamais exposés publiquement</strong> : seuls le statut et le badge sont visibles, pas les pièces.</span>
            </li>
            <li className="flex gap-2">
              <span className="text-bretagne">•</span>
              <span>Conservation limitée et <strong>suppression sur simple demande</strong>.</span>
            </li>
          </ul>
        </section>

        {/* Captain CTA */}
        <section className="mt-6 bg-ocean text-blanc rounded-2xl p-7 md:p-9 text-center">
          <h2 className="font-display text-2xl md:text-3xl font-bold">Vous êtes capitaine&nbsp;?</h2>
          <p className="mt-2 text-blanc/80 max-w-xl mx-auto leading-relaxed">
            Faites vérifier votre profil (c'est <strong>optionnel et gratuit</strong>) pour gagner la confiance des pêcheurs.
          </p>
          <Link to="/publier" data-testid="confiance-cta">
            <Button className="mt-5 bg-blanc hover:bg-blanc/90 text-ocean font-bold rounded-full px-7 py-5 tactile">
              Vérifier mon profil <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </section>

        {/* Cobaturage footer */}
        <section className="mt-4 bg-blanc border border-brume rounded-2xl p-5 flex gap-3">
          <Fish className="w-5 h-5 text-corail shrink-0 mt-0.5" />
          <div className="text-sm text-ocean/85 leading-relaxed">
            <strong className="text-ocean">Cobaturage entre passionnés, pas un commerce.</strong>{" "}
            Partage de frais entre passionnés, pas un service commercial. Les participations couvrent
            uniquement les frais réels de la sortie.
            <div className="mt-1 text-[11px] text-bretagne/80">
              Aucun paiement en ligne, aucune commission. Les participations se règlent directement
              entre membres et couvrent uniquement les frais réels.
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
